<template>
  <CheckoutSection>
    <template #header>{{ $t('pages.checkout.digital_section_title') }}</template>
    <CheckoutCartItem v-for="item in digitalItems" :key="item.id">
      <template #headerTitle>{{ item.pica_service.event_name }}</template>
      <template #headerSubtitle>
        {{ $t('pages.checkout.summary_picacode') }} <span class="code">{{ item.checkin.picacode }}</span>
      </template>
      <template #console>
        <b-button v-if="item.checkin.can_redeem_voucher && !item.checkin.voucher_code"
                  @click="$emit('open-modal-voucher', item.checkin.id)" class="text-uppercase"
                  size="sm" variant="dark" v-b-tooltip.hover :title="$t('pages.checkout.btn_add_voucher_tooltip')">
          {{ $t('pages.checkout.btn_add_voucher') }}
        </b-button>
        <b-button v-if="item.videos_count"
                  @click="deleteVideosFromDigitalItemFromCart(item.id)" class="mr-2"
                  size="sm" variant="light">
          {{ $t('pages.checkout.btn_delete_video') }}
        </b-button>
        <router-link :to="{ name: 'checkins-detail', params: { picacode: item.checkin.picacode }}" class="rounded-icon"
                     :title="$t('pages.checkout.action_edit_item')">
          <Icon name="edit" width="20" class="text-secondary"/>
        </router-link>
        <a href="#" @click.prevent="openModalConfirm(item.id)" class="rounded-icon"
           :title="$t('pages.checkout.action_delete_item')">
          <Icon name="trash" width="20" class="text-secondary"/>
        </a>
      </template>
      <div class="cart-item--content">
        <swiper-slider :slides-per-view="4.5"
                       :space-between="16"
                       :slides-per-group="2"
                       :breakpoints="cartSliderBreakpoints"
                       :loop="false"
                       :lazy="false"
                       css-mode
                       :simulate-touch="false"
                       :selected-index="0"
                       :extra-class="'slider-cart'">
          <template v-slot:slides>
            <div class="swiper-slide" v-for="(slide, index) in item.photos" :key="index">
              <div class="cart-item--slide">
                <a href="#" class="cart-item--delete" @click.prevent.stop="openModalConfirm(item.id, slide.id)">
                  <icon name="closeCircle" width="24"/>
                </a>
                <div class="cart-item--image-container">
                  <img :src="slide.image_url" alt="" class="cart-item--image">
                </div>
              </div>
            </div>
          </template>
        </swiper-slider>
      </div>

      <template #footerItems v-if="item.order_type.includes('photolist')">
                <span v-if="item.photos_count > 0">
                    {{ $tc('pages.checkout.digital_amount_n_photos', item.photos_count) }}
                </span>
        <span v-if="item.photos_count > 0 && item.videos_count > 0"> + </span>
        <span v-if="item.videos_count > 0">
                    {{ $tc('pages.checkout.digital_amount_n_videos', item.videos_count) }}
                </span>
      </template>
      <template #footerItems v-else-if="item.order_type === 'photopackage'">
        {{ $t('pages.checkout.digital_amount_purchased_items') }}
      </template>
      <template #footerItems v-else-if="item.order_type.includes('photopackage')">
        {{ $t('pages.checkout.digital_amount_purchased_items') }}
      </template>
      <template #footerPrice>

        <div> {{ item.price_formatted }}</div>
        <del v-if="item.price !== item.price_original" class="pr-2 text-primary d-block"
             v-b-tooltip.hover :title="item.checkin.voucher_code">
          {{ item.price_original_formatted }}
        </del>
      </template>
    </CheckoutCartItem>

    <ModalCheckoutRemoveDigitalItem :modal-id="modalRemoveItemId"
                                    :digital-item-id="digitalItemId"
                                    :photo-id="photoId"
                                    @confirm="confirmDeletion"/>
  </CheckoutSection>
</template>


<script>
import ModalCheckoutRemoveDigitalItem from '@/components/ModalCheckoutRemoveDigitalItem'
import CheckoutSection from '@/components/CheckoutSection'
import CheckoutCartItem from '@/components/CheckoutCartItem'
import SwiperSlider from "@/components/SwiperSlider.vue";
import {mapActions} from "vuex";

export default {
  components: {
    SwiperSlider,
    CheckoutSection,
    CheckoutCartItem,
    ModalCheckoutRemoveDigitalItem
  },

  props: ['digitalItems'],

  data() {
    return {
      digitalItemId: null,
      photoId: null,
      cartSliderBreakpoints: {
        140: {
          slidesPerView: 2.5,
          slidesPerGroup: 1
        },
        780: {
          slidesPerView: 2.5,
          slidesPerGroup: 1
        },
        1024: {
          slidesPerView: 3.5,
          slidesPerGroup: 1

        },
        1280: {
          slidesPerView: 4.5,
          slidesPerGroup: 2
        }
      }
    }
  },

  created() {
    this.modalRemoveItemId = 'bv-modal-checkout-remove-digitalitem'
    this.calculateDigitalItemsCount()
  },

  methods: {
    ...mapActions('cart', ['deleteDigitalItem', 'deletePhotosFromDigitalItem', 'deleteVideosFromDigitalItem']),
    calculateDigitalItemsCount() {
      this.digitalItems.forEach(i => {
        if (i.photos) {
          i['photos_count'] = i.photos.filter(j => j.media_type === 'photo').length
          i['videos_count'] = i.photos.filter(j => j.media_type === 'video').length
        }
      })
    },
    openModalConfirm(itemId, photoId) {
      this.digitalItemId = itemId
      this.photoId = photoId
      this.$bvModal.show(this.modalRemoveItemId)
    },
    emptyItemIdToRemove() {
      this.$nextTick(() => {
        this.digitalItemId = null
        this.photoId = null
        this.calculateDigitalItemsCount()
      })
    },
    async confirmDeletion() {
      if (this.digitalItemId && this.photoId) {
        await this.deletePhotosFromDigitalItemFromCart()
      } else {
        await this.deleteDigitalItemFromCart()
      }
    },
    async deleteDigitalItemFromCart() {
      const digitalItemId = this.digitalItemId
      await this.deleteDigitalItem(digitalItemId)
      this.emptyItemIdToRemove()
      this.$bvToast.toast(this.$t('notifications.item_removed_from_cart'))
    },
    async deletePhotosFromDigitalItemFromCart() {
      let photoIds = [this.photoId]
      await this.deletePhotosFromDigitalItem({
        digitalItemId: this.digitalItemId,
        photoIds: photoIds
      })
      this.emptyItemIdToRemove()
      this.$bvToast.toast(this.$t('notifications.item_removed_from_cart'))
    },
    async deleteVideosFromDigitalItemFromCart(digitalItemId) {
      await this.deleteVideosFromDigitalItem(digitalItemId)
      this.$bvToast.toast(this.$t('notifications.item_removed_from_cart'))
    }
  }
}
</script>
